import { CostFeeType, CostRateInput, CostScheduleGroupKey } from '@bariendo/lib/payables';
import { Box, Chip, Group, Loader, SegmentedControl } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNow } from '@/hooks/useNow';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { alert } from '@/services/alert';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { shortName } from '../imports/manualEntry';
import { formatRateDate, rateSeries } from '../rates/rateSchedule';
import { RateScheduleTable } from '../rates/RateScheduleTable';
import { RateSave, RatesDrawer, RatesDrawerSave } from '../rates/RatesDrawer';
import {
  COST_FEE_TYPES,
  COST_PROCEDURES,
  costProcedureLabel,
  DAILY_MINIMUM,
  isPartnerFee,
  PARTNER_PROCEDURES,
  toRateGroup,
} from './costSchedule';
import { useCostSchedule } from './useCostSchedule';

/** Payables → Cost schedule (BAR-3047): what we pay, one fee type at a time. */

const failure = (error: unknown) => (error instanceof Error ? error.message : 'unknown error');
const ALL_PARTNERS = 'all';

const toInput = (rate: RateSave): CostRateInput => ({
  procedure: rate.procedure,
  baseFee: rate.rate,
  baseCoversMinutes: rate.baseCoversMinutes,
  increment: rate.increment,
  incrementEveryMinutes: rate.incrementEveryMinutes,
});

export function CostScheduleView({ now }: { now?: Date }): ReactElement {
  const medplum = useMedplum();
  const resolvedNow = useNow(now);
  const today = dayjs(resolvedNow).format('YYYY-MM-DD');
  const [feeType, setFeeType] = useState<CostFeeType>('FACILITY');
  const [partner, setPartner] = useState(ALL_PARTNERS);
  const schedule = useCostSchedule(feeType);
  const { practitioners } = useProviderDirectory();
  const [drawer, setDrawer] = useState<{ key: string; mode: 'update' | 'correct'; opened: number }>();
  const [saving, setSaving] = useState(false);
  const partnerFee = isPartnerFee(feeType);
  const meta = COST_FEE_TYPES.find((type) => type.value === feeType) ?? COST_FEE_TYPES[0];

  useEffect(() => {
    if (schedule.error) alert.error(`Couldn't load the cost schedule: ${schedule.error}`);
  }, [schedule.error]);

  const partners = useMemo(
    () =>
      [...new Map(schedule.groups.map((group) => [group.payeeRef, group.payeeName ?? group.payeeRef])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1])
      ),
    [schedule.groups]
  );
  const shownGroups = useMemo(
    () =>
      schedule.groups
        .filter((group) => !partnerFee || partner === ALL_PARTNERS || group.payeeRef === partner)
        .map((group) => ({ ...toRateGroup(group), source: group })),
    [schedule.groups, partner, partnerFee]
  );
  const open = shownGroups.find((group) => group.key === drawer?.key);

  const whoName = (reference: string): string => {
    const practitioner = practitioners.find((candidate) => getReferenceString(candidate) === reference);
    return practitioner ? shortName(practitioner) : '—';
  };
  const procedures = partnerFee ? PARTNER_PROCEDURES : COST_PROCEDURES;

  // A failed write re-reads the schedule: one that timed out may still have landed (BAR-3125).
  const call = <T,>(input: Record<string, unknown>) =>
    fetchBotWrite<T>(medplum, 'create-treatment-payment', input, schedule.reload);
  const keyOf = (): CostScheduleGroupKey | undefined =>
    open && { feeType: open.source.feeType, payeeRef: open.source.payeeRef, location: open.source.location };

  const save = async (change: RatesDrawerSave): Promise<void> => {
    const group = keyOf();
    if (!open || !group) return;
    setSaving(true);
    try {
      if (change.mode === 'update') {
        const result = await call<{ scheduled: number; replacedScheduled: boolean }>({
          operation: 'updateCostRates',
          costScheduleGroup: group,
          effectiveFrom: change.effectiveFrom,
          costRates: change.rates.map(toInput),
        });
        showUndoToast(
          `Rates updated · ${open.name} · from ${formatRateDate(change.effectiveFrom)}`,
          // An update that replaced an earlier scheduled one can't bring it back.
          result.scheduled > 0 && !result.replacedScheduled
            ? () =>
                call({ operation: 'removeScheduledCostRates', costScheduleGroup: group })
                  .then(schedule.reload)
                  .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
            : undefined
        );
      } else {
        const { correctedIds } = await call<{ correctedIds: string[] }>({
          operation: 'correctCostRates',
          costScheduleGroup: group,
          costRates: change.rates.map(toInput),
          reason: change.reason,
        });
        showUndoToast(`Corrected · ${open.name}`, () =>
          call({ operation: 'undoCostRateCorrection', rateIds: correctedIds })
            .then(schedule.reload)
            .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
        );
      }
      setDrawer(undefined);
      schedule.reload();
    } catch (error) {
      alert.error(`Couldn't save the rates: ${failure(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const removeScheduled = async (): Promise<void> => {
    const group = keyOf();
    if (!open || !group) return;
    try {
      const removed = await call<{ effectiveFrom: string | null; rates: CostRateInput[] }>({
        operation: 'removeScheduledCostRates',
        costScheduleGroup: group,
      });
      setDrawer(undefined);
      schedule.reload();
      if (removed.effectiveFrom) {
        const from = removed.effectiveFrom;
        showUndoToast(`Update from ${formatRateDate(from)} removed · ${open.name}`, () =>
          call({
            operation: 'updateCostRates',
            costScheduleGroup: group,
            effectiveFrom: from,
            costRates: removed.rates,
          })
            .then(schedule.reload)
            .catch((error: unknown) => alert.error(`Couldn't undo: ${failure(error)}`))
        );
      }
    } catch (error) {
      alert.error(`Couldn't remove the scheduled update: ${failure(error)}`);
    }
  };

  return (
    <Box style={{ flex: 1, overflow: 'auto' }}>
      <Group gap={12} px={16} pb={12} wrap="wrap">
        <SegmentedControl
          size="xs"
          data={COST_FEE_TYPES.map(({ value, label }) => ({ value, label }))}
          value={feeType}
          onChange={(value) => {
            setFeeType(value as CostFeeType);
            setPartner(ALL_PARTNERS);
          }}
        />
        {partnerFee && (
          <Chip.Group multiple={false} value={partner} onChange={(value) => setPartner(value as string)}>
            <Group gap={6}>
              <Chip size="xs" value={ALL_PARTNERS}>
                {feeType === 'FACILITY' ? 'All facilities' : 'All anesthesia partners'}
              </Chip>
              {partners.map(([ref, name]) => (
                <Chip key={ref} size="xs" value={ref}>
                  {name}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        )}
      </Group>
      {schedule.loading && shownGroups.length === 0 ? (
        <Box p={16}>
          <Loader size="sm" />
        </Box>
      ) : schedule.error && shownGroups.length === 0 ? (
        <LoadFailed what="the cost schedule" error={schedule.error} onRetry={schedule.reload} />
      ) : (
        <RateScheduleTable
          variableFees={partnerFee}
          rateLabel={meta.rateLabel}
          groups={shownGroups}
          today={today}
          procedureLabel={costProcedureLabel}
          whoName={whoName}
          onUpdate={(key) => setDrawer({ key, mode: 'update', opened: Date.now() })}
          onCorrect={(key) => setDrawer({ key, mode: 'correct', opened: Date.now() })}
        />
      )}
      {open && drawer && (
        <RatesDrawer
          // A fresh draft each time the drawer opens.
          key={drawer.opened}
          opened
          variableFees={partnerFee}
          optionalProcedures={partnerFee ? [DAILY_MINIMUM] : []}
          rateLabel={meta.rateLabel}
          mode={drawer.mode}
          groupName={open.name}
          series={rateSeries(open.versions, today)}
          procedures={procedures}
          today={today}
          saving={saving}
          onClose={() => setDrawer(undefined)}
          onSave={(change) => void save(change)}
          onRemoveScheduled={() => void removeScheduled()}
        />
      )}
    </Box>
  );
}
